import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { useEffect, useRef, useState } from 'react';
import { SERVICE_AREA_COLORS, ServiceAreaType } from '../constants';

interface InitialShapeData {
  type: ServiceAreaType;
  vertices?: [number, number][];
  centroid?: [number, number];
  range?: number;
}

export interface ExistingShape {
  type: ServiceAreaType;
  vertices?: [number, number][];
  centroid?: [number, number];
  range?: number;
  color: string;
  strokeWidth?: number;
}

export interface DrawingManagerResult {
  drawingManager: google.maps.drawing.DrawingManager | null;
  currentOverlay: google.maps.Polygon | google.maps.Circle | null;
  clearCurrentOverlay: () => void;
  toggleExistingShapesVisibility: (visible: boolean) => void;
}

export function useDrawingManager(
  color = SERVICE_AREA_COLORS[0],
  onOverlayComplete?: (overlay: google.maps.Polygon | google.maps.Circle) => void,
  initialShapeData?: InitialShapeData,
  onOverlayClear?: () => void,
  existingShapes?: ExistingShape[]
): DrawingManagerResult {
  const map = useMap();
  const drawingLibrary = useMapsLibrary('drawing');
  const [drawingManager, setDrawingManager] = useState<google.maps.drawing.DrawingManager | null>(null);
  const [currentOverlay, setCurrentOverlay] = useState<google.maps.Polygon | google.maps.Circle | null>(null);
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
  const currentColorRef = useRef(color);
  const initialShapeCreatedRef = useRef(false);
  const existingOverlaysRef = useRef<(google.maps.Polygon | google.maps.Circle)[]>([]);

  // Function to clear existing overlays
  const clearExistingOverlays = () => {
    existingOverlaysRef.current.forEach(overlay => overlay.setMap(null));
    existingOverlaysRef.current = [];
  };

  // Function to toggle visibility of existing shapes
  const toggleExistingShapesVisibility = (visible: boolean) => {
    existingOverlaysRef.current.forEach(overlay => {
      overlay.setMap(visible ? map : null);
    });
  };

  // Function to clear the current overlay
  const clearCurrentOverlay = () => {
    if (currentOverlay) {
      currentOverlay.setMap(null);
      setCurrentOverlay(null);
      if (onOverlayClear) {
        onOverlayClear();
      }
    }
  };

  // Create existing shapes
  useEffect(() => {
    if (!map || !existingShapes || !drawingLibrary) {
      return;
    }

    clearExistingOverlays();

    existingShapes.forEach(shape => {
      const overlayOptions = {
        fillColor: shape.color,
        strokeColor: shape.color,
        fillOpacity: 0.1,
        strokeWeight: shape.strokeWidth || 1,
        editable: false,
        draggable: false,
        map,
        clickable: false
      };

      if (shape.type === ServiceAreaType.POLYGON && shape.vertices) {
        const paths = shape.vertices.map(([lat, lng]) => new google.maps.LatLng(lat, lng));
        const polygon = new google.maps.Polygon({
          ...overlayOptions,
          paths
        });
        existingOverlaysRef.current.push(polygon);
      } else if (shape.type === ServiceAreaType.CIRCLE && shape.centroid && shape.range) {
        const circle = new google.maps.Circle({
          ...overlayOptions,
          center: new google.maps.LatLng(shape.centroid[0], shape.centroid[1]),
          radius: shape.range
        });
        existingOverlaysRef.current.push(circle);
      }
    });

    return () => {
      clearExistingOverlays();
    };
  }, [map, existingShapes, drawingLibrary]);

  // Create initial shape if data is provided
  useEffect(() => {
    if (!map || !initialShapeData || initialShapeCreatedRef.current || !drawingLibrary || !drawingManagerRef.current) {
      return;
    }

    // Add a small delay to ensure drawing manager is fully initialized
    const timer = setTimeout(() => {
      try {
        clearCurrentOverlay();

        const overlayOptions = {
          fillColor: currentColorRef.current,
          strokeColor: currentColorRef.current,
          fillOpacity: 0.3,
          strokeWeight: 1.5,
          editable: true,
          draggable: true,
          map,
          zIndex: 2 // Ensure editable shape is above existing shapes
        };

        if (initialShapeData.type === ServiceAreaType.POLYGON && initialShapeData.vertices) {
          const paths = initialShapeData.vertices.map(([lat, lng]) => new google.maps.LatLng(lat, lng));

          const polygon = new google.maps.Polygon({
            ...overlayOptions,
            paths
          });

          setCurrentOverlay(polygon);

          // Trigger the DrawingManager's overlaycomplete event programmatically
          if (drawingManagerRef.current) {
            google.maps.event.trigger(drawingManagerRef.current, 'overlaycomplete', {
              type: google.maps.drawing.OverlayType.POLYGON,
              overlay: polygon
            });
          }

          // Listen for path changes (vertex edits)
          ['set_at', 'insert_at'].forEach(eventName => {
            google.maps.event.addListener(polygon.getPath(), eventName, () => {
              if (onOverlayComplete) onOverlayComplete(polygon);
            });
          });

          // Listen for polygon drag events (position changes)
          google.maps.event.addListener(polygon, 'dragend', () => {
            if (onOverlayComplete) onOverlayComplete(polygon);
          });

          if (onOverlayComplete) onOverlayComplete(polygon);
        } else if (
          initialShapeData.type === ServiceAreaType.CIRCLE &&
          initialShapeData.centroid &&
          initialShapeData.range
        ) {
          const circle = new google.maps.Circle({
            ...overlayOptions,
            center: new google.maps.LatLng(initialShapeData.centroid[0], initialShapeData.centroid[1]),
            radius: initialShapeData.range
          });

          setCurrentOverlay(circle);

          // Trigger the DrawingManager's overlaycomplete event programmatically
          if (drawingManagerRef.current) {
            google.maps.event.trigger(drawingManagerRef.current, 'overlaycomplete', {
              type: google.maps.drawing.OverlayType.CIRCLE,
              overlay: circle
            });
          }

          ['radius_changed', 'center_changed'].forEach(eventName => {
            google.maps.event.addListener(circle, eventName, () => {
              if (onOverlayComplete) onOverlayComplete(circle);
            });
          });

          if (onOverlayComplete) onOverlayComplete(circle);
        }

        initialShapeCreatedRef.current = true;
      } catch (error) {
        //Error creating initial shape
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [map, initialShapeData, drawingLibrary, drawingManager]);

  // Create DrawingManager only once
  useEffect(() => {
    if (!map || !drawingLibrary || drawingManagerRef.current) return;

    try {
      const newDrawingManager = new drawingLibrary.DrawingManager({
        drawingMode: null,
        drawingControl: false,
        circleOptions: {
          fillColor: currentColorRef.current,
          fillOpacity: 0.3,
          strokeWeight: 1.5,
          strokeColor: currentColorRef.current,
          editable: true,
          zIndex: 2
        },
        polygonOptions: {
          fillColor: currentColorRef.current,
          fillOpacity: 0.3,
          strokeWeight: 1.5,
          strokeColor: currentColorRef.current,
          editable: true,
          draggable: true,
          zIndex: 2
        }
      });

      newDrawingManager.setMap(map);
      drawingManagerRef.current = newDrawingManager;
      setDrawingManager(newDrawingManager);

      const overlayCompleteListener = google.maps.event.addListener(newDrawingManager, 'overlaycomplete', event => {
        if (event.overlay) {
          if (currentOverlay) {
            clearCurrentOverlay();
          }

          setCurrentOverlay(event.overlay);

          const overlayOptions = {
            fillColor: currentColorRef.current,
            strokeColor: currentColorRef.current,
            fillOpacity: 0.3,
            strokeWeight: 1,
            zIndex: 2
          };

          if (event.type === 'polygon') {
            const polygon = event.overlay as google.maps.Polygon;
            polygon.setOptions(overlayOptions);

            // Listen for path changes (vertex edits)
            ['set_at', 'insert_at'].forEach(eventName => {
              google.maps.event.addListener(polygon.getPath(), eventName, () => {
                if (onOverlayComplete) onOverlayComplete(polygon);
              });
            });

            // Listen for polygon drag events (position changes)
            google.maps.event.addListener(polygon, 'dragend', () => {
              if (onOverlayComplete) onOverlayComplete(polygon);
            });

            if (onOverlayComplete) onOverlayComplete(polygon);
          } else if (event.type === 'circle') {
            const circle = event.overlay as google.maps.Circle;
            circle.setOptions(overlayOptions);

            ['radius_changed', 'center_changed'].forEach(eventName => {
              google.maps.event.addListener(circle, eventName, () => {
                if (onOverlayComplete) onOverlayComplete(circle);
              });
            });

            if (onOverlayComplete) onOverlayComplete(circle);
          }
        }
        newDrawingManager.setDrawingMode(null);
      });

      // Only clean up on unmount
      return () => {
        google.maps.event.removeListener(overlayCompleteListener);
        if (currentOverlay) {
          clearCurrentOverlay();
        }
        clearExistingOverlays();
        if (drawingManagerRef.current) {
          drawingManagerRef.current.setMap(null);
          drawingManagerRef.current = null;
        }
      };
    } catch (error) {
      //Error creating DrawingManager
      return undefined;
    }
  }, [map, drawingLibrary]);

  // Handle color updates
  useEffect(() => {
    currentColorRef.current = color;

    const manager = drawingManagerRef.current;
    if (!manager) return;

    // Update drawing manager options for future shapes
    manager.setOptions({
      polygonOptions: {
        fillColor: color,
        fillOpacity: 0.3,
        strokeWeight: 1.5,
        strokeColor: color,
        editable: true,
        draggable: true,
        zIndex: 2
      },
      circleOptions: {
        fillColor: color,
        fillOpacity: 0.3,
        strokeWeight: 1.5,
        strokeColor: color,
        editable: true,
        zIndex: 2
      }
    });

    // Update existing overlay
    if (currentOverlay) {
      currentOverlay.setOptions({
        fillColor: color,
        strokeColor: color,
        fillOpacity: 0.3,
        strokeWeight: 1.5,
        zIndex: 2
      });
    }
  }, [color, currentOverlay]);

  return {
    drawingManager,
    currentOverlay,
    clearCurrentOverlay,
    toggleExistingShapesVisibility
  };
}
